import { Injectable } from '@angular/core';
import { Brand } from '~app/shared/enums/brand.enum';
import { sortString } from '~app/shared/functions/utils/utils.function';
import { CheckinState } from '~app/shared/enums/checkin-state.enum';
import { Country } from '~app/shared/models/country.model';
import { EffectItem } from '~app/shared/models/effectItem.model';
import { PhoneType } from '~app/shared/models/phoneType/phoneType.model';
import { Policy } from '~app/shared/models/policy/policy.model';
import { Port } from '~app/shared/models/port.model';
import { Ship } from '~app/shared/models/ship.model';
import { environment } from '~env';
import { MwService } from '../mw/mw.service';
import { StoreService } from '../store/store.service';
import { UtilsService } from '../utils/utils.service';
import { Assignment } from '../../models/assignment/assignment.model';

@Injectable({
  providedIn: 'root',
})
export class DataUtilsService {
  constructor(
    private readonly utils: UtilsService,
    private readonly mw: MwService,
    private readonly store: StoreService,
  ) {}

  public async getCheckinState(): Promise<CheckinState> {
    const assignmentList = await this.mw.getCrewAssignments().toPromise();
    const assignment = this.utils.getClosestAssignment(assignmentList);
    if (!assignment) return CheckinState.UNASSIGNED;

    if (!environment.featureFlags.checkin.includes(assignment.shipCode)) return CheckinState.DISABLED;

    const workflowState = await this.mw.getWorkflowState(assignment.shipCode, assignment.signonDate).toPromise();
    if (!workflowState) return CheckinState.UNASSIGNED;

    if (assignment.checkinEnabled === 'FALSE') return CheckinState.CLOSED;
    const precheckStatus = workflowState.payload.precheckStatus;
    if (precheckStatus === 0) return CheckinState.OPEN;
    if (precheckStatus > 0 && precheckStatus < 100) return CheckinState.PENDING;

    if (precheckStatus === 100) {
      const healthPercentage = workflowState.payload.medicalStatus;
      if (healthPercentage === 100) return CheckinState.COMPLETED;
      if (assignment.healthCheckEnabled === 'TRUE') return CheckinState.COMPLETED;
      return CheckinState.COMPLETED;
    }

    console.error('There was a problem calculating check-in status');
    return CheckinState.CLOSED;
  }

  public async ensureShipsContextData(): Promise<any> {
    return Promise.all([this.ensureShips(), this.ensurePorts()]);
  }

  public async ensureShips(): Promise<void> {
    if (!this.store.ships) {
      const shipsData = await this.mw.getShipList().toPromise();
      this.store.ships = shipsData.payload
        .map(shipData => new Ship(shipData))
        .sort((a, b) => sortString(a.code, b.code));
    }
  }

  public async ensurePorts(): Promise<void> {
    if (!this.store.ports) {
      const portsData = await this.mw.getPortCodeMaster().toPromise();
      this.store.ports = portsData.payload.map(portData => new Port(portData));
    }
  }

  public async ensureCountries(): Promise<void> {
    if (!this.store?.countries) {
      const countriesData = await this.mw.getCountryList().toPromise();
      this.store.countries = countriesData.payload?.countries.map(countryData => new Country(countryData));
    }
  }

  public async ensureEffectItems(): Promise<void> {
    if (!this.store.effectItems) {
      const effectItemsData = await this.mw.getCrewEffectsMaster().toPromise();
      this.store.effectItems = effectItemsData.payload.map(effectItem => new EffectItem(effectItem));
    }
  }

  public async ensurePhoneTypes(): Promise<void> {
    if (!this.store.phoneTypes) {
      const phoneTypesData = await this.mw.getPhoneTypeMaster().toPromise();
      this.store.phoneTypes = phoneTypesData.payload.phoneTypes.map(phoneTypeData => new PhoneType(phoneTypeData));
    }
  }

  public async ensureSymptomsAndQuestions(): Promise<void> {
    if (!this.store.symptoms) {
      const diseasesData = await this.mw.getHealthCheckSurveyContent().toPromise();
      this.store.symptoms = diseasesData.payload.symptoms;
      this.store.questions = diseasesData.payload.questions;
    }
  }

  public async ensureCurrentUserAssignments(): Promise<void> {
    if (!this.store.currentUser.assignments) {
      await this.ensureShipsContextData();

      const assignmentsData = await this.mw.getCrewAssignments().toPromise();
      const assignments = assignmentsData.payload.assignments.map(
        data => new Assignment(data, this.store.ships, this.store.ports),
      );
      this.store.currentUser.assignments = assignments;
    }
  }

  public async ensurePolicy(policyId: string, brand: Brand): Promise<void> {
    if (!this.store.policies.find(policy => policy.id === policyId && policy.brand === brand)) {
      const policyRes = await this.mw.getEmployeeAgreementInfo(policyId, brand).toPromise();
      const policy = new Policy(policyRes.payload.employeeAgreementInfo[0], brand);
      this.store.policies.push(policy);
    }
  }
}
